import { AxiosPromise, AxiosResponse } from 'axios';
import { apiService, manageFilters } from '@app/services';
import { IActiveFilter } from '@app/models';

class AppLibraryService {
    readonly apiBase: string = '/api/directory/Applications';

    getApps(orderBy: string, asc: string, top: number, skip: number, organizationId: string, isReset: boolean, filters?: IActiveFilter[]): AxiosPromise {
        return apiService.get(`${this.apiBase}/Detailed`, {
            params: {
                $orderBy: orderBy + ' ' + asc,
                $top: top,
                $skip: skip,
                $count: isReset,
                ...(filters ? { $filter: manageFilters(filters, organizationId) } : { $filter: manageFilters([], organizationId) })
            }
        })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    addWorkflowCatalogApp(webCatalogId: string, catalogId: number, customName: string, WorkflowValues: string, onError: Function, onSuccess: Function): AxiosPromise {
        return apiService
            .post(`${this.apiBase}/AddWorkflowCatalogApp`, {
                WebCatalogId: webCatalogId,
                CatalogId: catalogId,
                CustomName: customName,
                WorkflowValues: WorkflowValues
            },
                {
                    onError,
                    onSuccess
                }
            ).catch();
    }

    getApp(appId: string, onError?: Function, onSuccess?: Function): AxiosPromise {
        return apiService
            .get(
                `${this.apiBase}(${appId})?$expand=Groups,Attributes`,
                {
                    onError,
                    onSuccess
                }
            )
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    addCatalogApp(data: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        return apiService
            .post(`${this.apiBase}`,
                data,
                { onError: handleError, onSuccess: handleSuccess }
            )
            .catch();
    }

    updateApp(appId: string, data: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        return apiService
            .patch(`${this.apiBase}(${appId})`,
                data,
                { onError: handleError, onSuccess: handleSuccess }
            )
            .catch();
    }

    updateAppAudienceUris(appId: string, data: any): AxiosPromise {
        return apiService
            .post(`${this.apiBase}(${appId})/UpdateAudiences`, { audienceUris: data })
            .catch();
    }

    updateAppIcon = (data: any, handleError: Function, handleSuccess: Function, appId: string): AxiosPromise => {
        return apiService
            .post(`${this.apiBase}(${appId})/UploadImage`, data,
                { onError: handleError, onSuccess: handleSuccess, headers: { 'Content-Type': 'multipart/form-data' } })
            .catch();
    }

    getCatalogedIcons = (handleError: Function, handleSuccess: Function): AxiosPromise<AxiosResponse & { cancel: any }> => {
        return apiService
            .get(
                `${this.apiBase}/images`,
                { onError: handleError, onSuccess: handleSuccess })
            .catch();
    }

    deleteApps(jsonObject: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}/DeleteApplications`;

        return apiService.post(apiUrl,
            jsonObject,
            { onError: handleError, onSuccess: handleSuccess })
            .catch();
    }

    getCatalog(top: number, skip: number, isReset?: boolean, filters?: IActiveFilter[]): AxiosPromise {
        return apiService.get(`${this.apiBase}/Catalog`, {
            params: {
                $orderBy: 'DisplayName asc',
                $top: top,
                $skip: skip,
                $count: isReset,
                ...(filters ? { $filter: manageFilters(filters) } : { $filter: manageFilters([]) })
            }
        })
            .catch();
    }

    getWebCatalog(top: number, skip: number, shouldCount?: boolean, filters?: IActiveFilter[]): AxiosPromise {
        return apiService.get(`${this.apiBase}/WebCatalog`, {
            params: {
                $orderBy: 'DisplayName asc',
                $top: top,
                $skip: skip,
                $count: shouldCount,
                ...(filters ? { $filter: manageFilters(filters) } : { $filter: manageFilters([]) })
            }
        })
            .catch();
    }

    getCatalogCategories(): AxiosPromise {
        return apiService.get(`${this.apiBase}/WebCatalogCategories`)
            .catch();
    }

    getSpecificCatalogApp(name: string, handleSuccess: Function) {
        return apiService.get(`${this.apiBase}/Catalog`,
            {
                params: {
                    $filter: `DisplayName eq '${name}'`
                },
                onSuccess: handleSuccess
            })
            .catch();
    }

    getOffice365Apps(): AxiosPromise {
        return apiService.get(`${this.apiBase}/GetOffice365Apps`)
            .catch();
    }
}

export const appLibraryService = new AppLibraryService();
